/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, no-restricted-properties, warp-drive/no-legacy-request-patterns */
import Service, { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import moment from 'moment/moment';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import {
  findBy,
  mapBy,
  sortBy,
} from '@linc-technologies/linced-common/utils/arrays';

export default class AttendanceService extends Service {
  // Services
  @service
  attendanceCodes;

  @service
  dates;

  @service
  session;

  @service
  store;

  @alias('session.school.jurisdiction')
  jurisdiction;

  @computed('jurisdiction')
  get trackMovements() {
    return this.jurisdiction !== Jurisdiction.NZ_MOE;
  }

  recordForPeriod(personId, date, period) {
    let dateId = this.dates.getDate(date).get('id');
    return this.store
      .query('attendance/record', {
        personId,
        dateId,
        periodId: period.get('id'),
      })
      .then((records) => {
        return Promise.resolve(records[0]);
      });
  }

  createAttendanceRecord(person, dateString, period, status, notes) {
    // date in format of YYYY-MM-DD
    let dateObj = this.get('dates.calendar').find((d) => d.date === dateString);
    let record = this.store.createRecord('attendance/record', {
      schoolId: this.get('session.school'),
      personId: person,
      authorId: this.get('session.person'),
      code: status.code,
      notes: notes,
      dateId: dateObj,
      date: dateString,
      periodId: period,
    });

    if (this.trackMovements) {
      let time = moment(
        `${dateString}T${period.startTime}`,
        'YYYY-MM-DDTHH:mm',
      ).unix();
      record.set('movements', [
        {
          time,
          code: status.code,
          authorId: this.get('session.person.id'),
          notes,
        },
      ]);
    }

    return record;
  }

  updateAttendanceRecord(code, notes, reason, record, effectiveFrom) {
    if (isEmpty(record)) {
      return;
    }
    if (this.trackMovements) {
      if (code === record.code && isEmpty(effectiveFrom)) {
        // Same code - a note was added.  Apply it to the latest movement.
        effectiveFrom = record.get('movements.lastObject.time');
      } else if (
        isEmpty(notes) &&
        !['All day attendance', 'Added note'].includes(reason)
      ) {
        // As we handle new movements via the audit flow, the notes arrive as
        // the reason (except all-day attendance and note-only updates).
        notes = reason;
      }
      let existingMovement = findBy(record.movements, 'time', effectiveFrom);
      if (existingMovement) {
        // We're updating/overwriting an existing movement.
        record.setProperties({
          reason,
          effectiveFrom,
        });
        existingMovement.setProperties({
          time: effectiveFrom,
          code,
          authorId: this.get('session.person.id'),
          notes,
        });
      } else {
        // We're adding a new movement.
        record.movements.createFragment({
          time: effectiveFrom,
          code,
          authorId: this.get('session.person.id'),
          notes,
        });

        // Set the top-level `effectiveFrom` field to the new movement's time.
        record.set('effectiveFrom', effectiveFrom);
      }

      // Ensure movements are sorted chronologically.
      record.set('movements', sortBy(record.movements, 'time'));

      if (record.movements.lastObject.time === effectiveFrom) {
        // Only update the record's main code if this is the latest movement.
        record.setProperties({
          code,
          notes,
          reason,
          authorId: this.get('session.person'),
        });
      }
    } else {
      record.setProperties({
        code,
        notes,
        reason,
        authorId: this.get('session.person'),
      });
    }

    return record;
  }

  // calculateHalfDayAnalysis derives half-day attendance totals from a set of
  // day codes and the attendance records behind them.
  //
  // `filters` restricts the analysis to a subset of attendance codes.  It is
  // only supplied by the attendance analysis list
  calculateHalfDayAnalysis(dayCodes, records, filters) {
    let isFiltered = !isEmpty(filters);
    let filterCodes = isFiltered
      ? filters
      : mapBy(this.attendanceCodes.allCodes, 'code');
    let codes = {
      counts: {
        P: 0,
        J: 0,
        U: 0,
        I: 0,
        O: 0,
      },
      partialAbsences: 0,
      minorPartialAbsences: 0,
      present: 0,
      absent: 0,
    };

    dayCodes.forEach((dayCode) => {
      let isExempt = dayCode.code === 'H' || dayCode.code === 'M';
      let present = dayCode.present || 0;
      let unjustifiablyAbsent = dayCode.unjustifiablyAbsent || 0;
      let justifiedAbsent = dayCode.justifiedAbsent || 0;
      let hasMinorPartialAbsence = dayCode.hasMinorPartialAbsence;
      if (typeof hasMinorPartialAbsence !== 'boolean') {
        hasMinorPartialAbsence = false;
      }

      let updateData = (key, value, isAbsent) => {
        if (isAbsent) {
          codes.counts[key] += value;
          codes.absent += value;
        } else {
          codes.counts[key] += value;
          codes.present += value;
        }
      };

      let recordsForDate = records.filter((record) => {
        return record.date === dayCode.date || record.dateId === dayCode.dateId;
      });
      if (isEmpty(recordsForDate)) {
        return;
      }
      let countedCodes = [];
      let hasMatchingMovement = false;
      filterCodes.forEach((code) => {
        let truancyCode = this.attendanceCodes.truancyMap[code];
        let hasCode = recordsForDate.some((record) => {
          return !isEmpty(findBy(record.movements, 'code', code));
        });
        if (hasCode) {
          hasMatchingMovement = true;
          if (truancyCode === 'P' && !countedCodes.includes(truancyCode)) {
            updateData('P', present, false);
            countedCodes.push(truancyCode);
          }
          if (truancyCode === 'J' && !countedCodes.includes(truancyCode)) {
            updateData('J', justifiedAbsent, true);
            countedCodes.push(truancyCode);
          }
          if (truancyCode === 'U' && !countedCodes.includes(truancyCode)) {
            updateData('U', unjustifiablyAbsent, true);
            countedCodes.push(truancyCode);
          }
        }
      });

      // A partial absence is a property of the day code itself rather than of
      // any individual record, which is why the calendar can render it straight
      // from `hasMinorPartialAbsence` and why the back end reports it as a
      // plain count on `attendance/day-code`.  A minor partial absence is under
      // 120 minutes, which usually leaves both half-days intact, so it must not
      // be gated on `present` - only the >= 120 minute case is.
      //
      // Only the filtered caller correlates this against records: there a day
      // needs a movement matching one of the filtered codes before its partial
      // absence is attributed to them.  Unfiltered callers take the day code at
      // face value, as the calendar does.
      if (!isExempt && (!isFiltered || hasMatchingMovement)) {
        if (hasMinorPartialAbsence) {
          codes.minorPartialAbsences++;
        } else if (dayCode.present === 1) {
          codes.partialAbsences++;
        }
      }
    });

    return {
      counts: codes.counts,
      present: codes.present / 2,
      absent: codes.absent / 2,
      partialAbsences: codes.partialAbsences,
      minorPartialAbsences: codes.minorPartialAbsences,
      total: (codes.present + codes.absent) / 2,
    };
  }
}
